import { FormControlLabel, Stack, Switch } from '@mui/material';
import {
  DirectionsRenderer,
  GoogleMap,
  Marker as MarkerCurrentLocation
} from '@react-google-maps/api';
import Marker from './marker';
import styled from 'styled-components';
import { ButtonCustom } from 'app/components/elements/ButtonCustom';
import { useInjection } from './hooks';
import ModalDetailLead from 'app/components/elements/Modal/ModalDetailLead';
import FilterModal from 'app/components/elements/ModalFilter';
import { LeadMap } from './type';
import React, { memo, useEffect, useRef, useState } from 'react';
import { ButtonFilter } from '../../components/elements/ButtonFilter';
import { useMapView } from 'contexts/map';

const RenderMap = memo(
  ({
    isLoaded,
    center,
    onLoad,
    onUnmount,
    leadsMap,
    activeIndex,
    onUpdateActiveIndex,
    onHold,
    currentLocation,
    directions,
    onGetDirection,
    formValue,
    onOpenModalFilter
  }: {
    isLoaded: boolean;
    center?: {
      lat: number;
      lng: number;
    };
    onLoad: (map: google.maps.Map) => void;
    onUnmount: () => void;
    leadsMap: LeadMap[];
    activeIndex: number[];
    onGetDirection: (newActiveIndex?: number[] | undefined) => void;
    directions?: google.maps.DirectionsResult;
    currentLocation?: {
      lat: number;
      lng: number;
    };
    onHold: (index: number) => void;
    onUpdateActiveIndex: (index: number) => void;
    formValue: {
      searchText: string;
      earlyMorning: boolean | undefined;
      doorKnock: boolean | undefined;
      status: string[];
      types: string[];
      sortBy: string | null;
      order: string | null;
      page: number;
      category: string | undefined;
      size: number;
    };
    onOpenModalFilter: () => void;
  }) => {
    const [selectPinEnabled, setSelectPinEnabled] = useState(false);
    const [zoom, setZoom] = useState<number>(5);
    const mapRef = useRef<google.maps.Map | null>(null);

    // When currentLocation becomes available, pan and zoom to it
    useEffect(() => {
      if (isLoaded && currentLocation && mapRef.current) {
        mapRef.current.panTo(currentLocation);
        mapRef.current.setZoom(15);
        setZoom(15);
      }
    }, [isLoaded, currentLocation]);
    const { mapView, setMapView } = useMapView();
    return (
      <Stack height={'100%'}>
        {isLoaded && (center || currentLocation) && (
          <GoogleMap
            center={mapView?.center || currentLocation || center}
            zoom={mapView?.zoom || zoom}
            onIdle={() => {
              if (mapRef.current) {
                const center = mapRef.current.getCenter()?.toJSON();
                const zoom = mapRef.current.getZoom();
                if (center && zoom) {
                  if (
                    mapView?.center.lat !== center.lat ||
                    mapView?.center.lng !== center.lng ||
                    mapView?.zoom !== zoom
                  ) {
                    setMapView({ center, zoom });
                  }
                }
              }
            }}
            onLoad={(map) => {
              mapRef.current = map;
              onLoad(map);
              if (mapView) {
                map.panTo(mapView.center);
                map.setZoom(mapView.zoom);
                return;
              }
              if (!mapView && currentLocation) {
                map.panTo(currentLocation);
                map.setZoom(15);
                setZoom(15);
              }
            }}
            onUnmount={() => {
              mapRef.current = null;
              onUnmount();
            }}
            options={{ disableDefaultUI: true, clickableIcons: false }}
            mapContainerStyle={{ height: '100vh' }}>
            {leadsMap
              .filter(
                (item) =>
                  item.latitude >= -90 &&
                  item.latitude <= 90 &&
                  item.longitude >= -180 &&
                  item.longitude <= 180
              )
              .map((item, index) => (
                <Marker
                  isFlag={!!item.flag}
                  item={item}
                  key={`map${index}`}
                  activeIndex={activeIndex.indexOf(index)}
                  isActive={activeIndex.includes(index)}
                  onPointerUp={() => {
                    if (selectPinEnabled) {
                      onUpdateActiveIndex(index);
                    }
                  }}
                  onHold={() => {
                    onHold(index);
                  }}
                />
              ))}

            {currentLocation && <MarkerCurrentLocation position={currentLocation} />}

            {directions && (
              <DirectionsRenderer directions={directions} options={{ suppressMarkers: true }} />
            )}
            {activeIndex.length > 0 && (
              <ButtonRoute
                onClick={() => {
                  onGetDirection();
                }}>
                Route
              </ButtonRoute>
            )}
            <BoxNotifyFilter>
              <Stack direction={'row'} gap={1.25} alignItems="center">
                <ButtonFilter
                  isFiltering={
                    !(
                      !formValue.doorKnock &&
                      !formValue.earlyMorning &&
                      !formValue.status.length &&
                      !formValue.types.length
                    )
                  }
                  onClick={onOpenModalFilter}
                />
              </Stack>
            </BoxNotifyFilter>
            <BoxNotifySelectPin>
              <FormControlLabel
                control={
                  <Switch
                    color="secondary"
                    checked={selectPinEnabled}
                    onChange={() => setSelectPinEnabled((prev) => !prev)}
                  />
                }
                label="Route"
              />
            </BoxNotifySelectPin>
            <BoxNotify>
              <Item>
                <Dot color="#9747FF" />
                <Text>New</Text>
              </Item>
              <Item>
                <Dot color="blue" />
                <Text>Working</Text>
              </Item>
              <Item>
                <Dot color="#FFAA47" />
                <Text>Attention</Text>
              </Item>
              <Item>
                <Dot color="#FF4773" />
                <Text>Aged</Text>
              </Item>
            </BoxNotify>
          </GoogleMap>
        )}
      </Stack>
    );
  }
);
type Props = {
  isAdmin?: boolean;
};
function MapPage({ isAdmin }: Props) {
  const {
    isLoaded,
    currentLocation,
    activeIndex,
    onLoad,
    onUnmount,
    directions,
    onGetDirection,
    onClose,
    open,
    onFilter,
    formValue,
    leadsMap,
    center,
    onUpdateActiveIndex,
    onHold,
    isOpenModalDetailLead,
    onOpenModalFilter,
    onCloseModalDetailLead,
    handleDeleteNoteLead,
    handleEditNote,
    itemMVS,
    handleAddNewNote,
    optionsUser,
    navigateDetailLead
  } = useInjection({ isAdmin });
  return (
    <Stack width={'100%'} style={{ overflow: 'hidden', height: '100%' }}>
      <RenderMap
        formValue={formValue}
        onOpenModalFilter={onOpenModalFilter}
        onGetDirection={onGetDirection}
        onHold={onHold}
        onUpdateActiveIndex={onUpdateActiveIndex}
        center={center}
        leadsMap={leadsMap}
        directions={directions}
        onUnmount={onUnmount}
        onLoad={onLoad}
        activeIndex={activeIndex}
        isLoaded={isLoaded}
        currentLocation={currentLocation}
      />
      <FilterModal
        optionsUser={optionsUser}
        isAdmin={isAdmin}
        isShowChecked
        isShowStatus
        open={open}
        onClose={onClose}
        onSubmit={onFilter}
        value={formValue}
      />
      <ModalDetailLead
        itemMVS={itemMVS?.find((item) => item.fileId === isOpenModalDetailLead?.fileId)}
        navigateDetail={navigateDetailLead}
        handleDeleteNoteLead={handleDeleteNoteLead}
        handleEditNote={handleEditNote}
        handleAddNewNote={handleAddNewNote}
        isOpen={isOpenModalDetailLead}
        onClose={onCloseModalDetailLead}
      />
    </Stack>
  );
}

export default MapPage;

const ButtonRoute = styled(ButtonCustom)`
  color: #d2d2cf;
  font-weight: 600;
  font-size: 16px;
  height: 32px;
  padding-inline: 16px;
  background: #22374a;
  border-radius: 100px;
  position: absolute;
  bottom: 16px;
  right: 16px;
  cursor: pointer;
  outline: none;
  border: none;
  width: fit-content;
`;

const BoxNotify = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  background: white;
  width: 130px;
  z-index: 9;
  border: 1px solid #22374a;
  border-radius: 10px;
  padding: 10px 10px 10px 10px;
`;
const BoxNotifyFilter = styled.div`
  position: absolute;
  bottom: 230px;
  left: 16px;
  background: white;
  z-index: 9;
  border: 1px solid #22374a;
  border-radius: 10px;
  padding: 10px 10px 10px 10px;
  align-items: end;
  justify-content: flex-end;
`;

const BoxNotifySelectPin = styled.div`
  position: absolute;
  bottom: 160px;
  left: 16px;
  background: white;
  z-index: 9;
  border: 1px solid #22374a;
  border-radius: 10px;
  padding: 10px 0px 10px 10px;
  width: 130px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 5px;
`;

const Dot = styled.div<{ color: string }>`
  width: 12px;
  height: 12px;
  border-radius: 12px;
  background: ${({ color }) => color};
`;

const Text = styled.span`
  font-weight: 400;
  font-size: 16px;
  color: #000;
`;
//
// const TitleStyled = styledMui(Typography)(() => ({
//   fontSize: 20,
//   fontWeight: 700,
//   lineHeight: '25px',
//   marginRight: 'auto'
// }));
//
// const Header = styled.div`
//   display: flex;
//   align-items: center;
//   padding-inline: 16px;
//   padding-bottom: 20px;
//   position: sticky;
//   top: 0;
//   width: 100%;
//   background: white;
//   z-index: 999;
// `;
//
// const HeaderRight = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 19px;
// `;
